import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Layout from '../../components/Layout';
import { truckingBillAPI } from '../../api/waybillAPI';
import { exportToCSV } from '../../utils/csvExport';
import { formatPHP, formatDatePH } from '../../utils/formatters';
import { useAuth } from '../../context/AuthContext';
import ReportToolbar from '../../shared/ReportToolbar';

const BUCKET_COLUMNS = [
  { key: 'b1_30', label: '1-30' },
  { key: 'b31_60', label: '31-60' },
  { key: 'b61_90', label: '61-90' },
  { key: 'b91_over', label: '91+' },
];

const cell = (n) => (n === 0 ? '—' : formatPHP(n));

const shipmentTypeLabel = (shipmentType) => (shipmentType === 'TRUCKING' ? 'Trucking' : 'Freight Forwarding');

// FF/Trucking toggle, styled like shared/SegmentToggle.jsx but with the two
// labels this report actually supports (that component's ALL/STANDARD/TRUCKING
// options don't apply here — the backend requires a single BillTypeId).
function ShipmentTypeToggle({ value, onChange }) {
  return (
    <div className="flex items-center gap-1 border border-ink-line rounded-md p-1 bg-white">
      {['FF', 'TRUCKING'].map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            value === opt ? 'bg-primary-600 text-white' : 'text-ink-body hover:bg-ink-canvas'
          }`}
        >
          {shipmentTypeLabel(opt)}
        </button>
      ))}
    </div>
  );
}

const CHILD_COL_COUNT = 2 + BUCKET_COLUMNS.length; // label + buckets + total

function ChildSkeleton() {
  return (
    <tr>
      <td colSpan={CHILD_COL_COUNT} className="px-4 py-3">
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-4 bg-ink-line/40 rounded animate-pulse" />
          ))}
        </div>
      </td>
    </tr>
  );
}

// Renders one row per invoice, with the SAME column layout as the parent
// summary row (label, then the 4 bucket columns, then total) so amounts
// line up under the same columns. Per spec, an invoice's amount renders
// only in its own bucket column — the total column stays blank for child rows.
function CustomerDetailRows({ customerId, shipmentType, dateFrom, dateTo }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ar-detail', customerId, shipmentType, dateFrom, dateTo],
    queryFn: () => truckingBillAPI.getARCustomerDetail(customerId, { shipmentType, dateFrom, dateTo })
      .then((res) => res.data?.data || []),
  });

  if (isLoading) return <ChildSkeleton />;
  if (isError) {
    return (
      <tr>
        <td colSpan={CHILD_COL_COUNT} className="px-4 py-3 text-danger-500 text-sm">Failed to load invoices for this customer.</td>
      </tr>
    );
  }

  if (!data || data.length === 0) {
    return (
      <tr>
        <td colSpan={CHILD_COL_COUNT} className="px-4 py-3 pl-10 text-ink-body text-sm">No invoices in range.</td>
      </tr>
    );
  }

  return (
    <>
      {data.map((inv) => (
        <tr key={inv.invoiceNo} className="bg-ink-canvas text-xs">
          <td className="px-4 py-2 pl-10 text-ink-body">
            <span className="font-medium text-ink">{inv.invoiceNo}</span>
            {' — Ref No. '}{inv.refNo || '—'}
            {' — due '}{formatDatePH(inv.dueDate)}
          </td>
          {BUCKET_COLUMNS.map((b) => (
            <td key={b.key} className="px-4 py-2 text-right text-ink-body">
              {inv.bucket === b.key ? cell(inv.outstanding) : '—'}
            </td>
          ))}
          <td className="px-4 py-2 text-right text-ink-body">—</td>
        </tr>
      ))}
    </>
  );
}

export default function ARAgingSummaryPage() {
  const { selectedClient } = useAuth();
  const accountingClientId = selectedClient?._id || localStorage.getItem('accountingClientId') || null;

  const [accountingClientName, setAccountingClientName] = useState(
    () => localStorage.getItem('accountingClientName') || ''
  );
  useEffect(() => {
    setAccountingClientName(localStorage.getItem('accountingClientName') || '');
  }, [accountingClientId]);

  // Defaults to "Custom" with blank dates — i.e. all invoice dates — rather
  // than a preset like YTD, since an aging report's whole point is surfacing
  // old outstanding debt that a Year-to-date default would hide.
  const [period, setPeriod] = useState({ preset: 'CUSTOM', startDate: '', endDate: '' });
  const [shipmentType, setShipmentType] = useState('FF');
  const [expanded, setExpanded] = useState({});

  const dateFrom = period.startDate || '';
  const dateTo = period.endDate || '';
  const params = { shipmentType, dateFrom, dateTo };
  if (accountingClientId) params.accountingClient = accountingClientId;

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ['ar-summary', dateFrom, dateTo, shipmentType, accountingClientId],
    queryFn: () => truckingBillAPI.getARSummaryView(params).then((res) => res.data?.data),
  });

  const rows = data?.rows || [];
  const grandTotal = data?.grandTotal || { b1_30: 0, b31_60: 0, b61_90: 0, b91_over: 0, total: 0 };

  const toggleCustomer = (customerId) => {
    setExpanded((prev) => ({ ...prev, [customerId]: !prev[customerId] }));
  };

  const rangeLabel = (dateFrom || dateTo)
    ? `${dateFrom ? formatDatePH(dateFrom) : 'earliest'} to ${dateTo ? formatDatePH(dateTo) : 'latest'}`
    : 'all invoice dates';

  // Same columns/shape as the print report — customer + bucket totals, not a
  // flat per-invoice list — plus a trailing Grand Total row. Uses the summary
  // data already loaded on screen, same source the print view reads from.
  const handleExportCSV = () => {
    const bucketCol = (n) => Number(n || 0).toFixed(2);
    const csvRows = rows.map((r) => ({
      Customer: r.customerName,
      '1-30': bucketCol(r.b1_30),
      '31-60': bucketCol(r.b31_60),
      '61-90': bucketCol(r.b61_90),
      '91+': bucketCol(r.b91_over),
      TOTAL: bucketCol(r.total),
    }));
    csvRows.push({
      Customer: 'Grand Total',
      '1-30': bucketCol(grandTotal.b1_30),
      '31-60': bucketCol(grandTotal.b31_60),
      '61-90': bucketCol(grandTotal.b61_90),
      '91+': bucketCol(grandTotal.b91_over),
      TOTAL: bucketCol(grandTotal.total),
    });
    const rangeSuffix = dateFrom || dateTo ? `${dateFrom || 'earliest'}-to-${dateTo || 'latest'}` : 'all-dates';
    exportToCSV(csvRows, `ar-aging-${shipmentType.toLowerCase()}-${rangeSuffix}.csv`);
  };

  // Print: builds the report as an HTML string and opens it in a new window,
  // same pattern as IncomeStatementComparative's handlePrint — no separate
  // routed print page, printed straight from the data already on screen.
  const handlePrint = () => {
    if (!data) return;
    const shipmentLabel = shipmentTypeLabel(shipmentType);

    const style = `<style>
      body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:40px}
      .hdr{text-align:center;margin-bottom:20px}
      .hdr .client{font-size:16px;font-weight:bold;margin-bottom:2px}
      .hdr h1{font-size:16px;margin:0 0 2px;font-weight:500}
      .hdr .sub{font-size:10.5px;color:#6b7280;margin-top:2px}
      table{width:100%;border-collapse:collapse;font-size:9pt}
      th{background:#f0f0f0;border:1px solid #ddd;padding:5px 8px;text-align:left}
      td{border:1px solid #ddd;padding:5px 8px}
      tr:nth-child(even){background:#fafafa}
      tfoot td{font-weight:bold;background:#f0f0f0}
      .tb{text-align:center;margin-bottom:20px}
      .tb button{padding:7px 18px;font-size:12px;cursor:pointer;background:#2b9dad;color:#fff;border:none;border-radius:4px}
      @media print{.tb{display:none}@page{size:A4 landscape;margin:12mm}}
    </style>`;

    const row = (r) => `<tr><td>${r.customerName}</td>${BUCKET_COLUMNS.map((b) => `<td style="text-align:right">${cell(r[b.key])}</td>`).join('')}<td style="text-align:right">${cell(r.total)}</td></tr>`;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>AR Aging Summary — ${shipmentLabel}</title>${style}</head><body>
      <div class="tb"><button onclick="window.print()">🖨 Print / Save as PDF</button></div>
      <div class="hdr">
        ${accountingClientName ? `<div class="client">${accountingClientName}</div>` : ''}
        <h1>AR Aging Summary — ${shipmentLabel}</h1>
        <div class="sub">For invoices ${rangeLabel}</div>
        <div class="sub">As of ${formatDatePH(new Date())}</div>
        <div class="sub">All amounts in Philippine peso (₱)</div>
      </div>
      <table>
        <thead><tr><th>Customer</th>${BUCKET_COLUMNS.map((b) => `<th style="text-align:right">${b.label}</th>`).join('')}<th style="text-align:right">TOTAL</th></tr></thead>
        <tbody>${rows.length === 0 ? `<tr><td colspan="${BUCKET_COLUMNS.length + 2}" style="text-align:center;padding:16px">No outstanding AR for this scope.</td></tr>` : rows.map(row).join('')}</tbody>
        ${rows.length > 0 ? `<tfoot><tr><td>Grand Total</td>${BUCKET_COLUMNS.map((b) => `<td style="text-align:right">${cell(grandTotal[b.key])}</td>`).join('')}<td style="text-align:right">${cell(grandTotal.total)}</td></tr></tfoot>` : ''}
      </table>
      <div style="margin-top:24px;text-align:center;font-size:10px;color:#6b7280;">Printed on ${new Date().toLocaleString('en-PH')}</div>
    </body></html>`;

    const win = window.open('', '_blank', 'width=1000,height=1000');
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 500);
  };

  return (
    <Layout pageTitle="AR Aging Summary">
      <div className="space-y-4">
        <Link to="/billing/ar" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700">
          <ArrowLeft className="h-4 w-4" />
          Back to AR
        </Link>

        <ReportToolbar
          showPeriod
          period={period}
          onPeriodChange={setPeriod}
          extra={<ShipmentTypeToggle value={shipmentType} onChange={setShipmentType} />}
          onExportCSV={data ? handleExportCSV : undefined}
          onPrint={data ? handlePrint : undefined}
          onRefresh={() => refetch()}
          refreshing={isFetching}
        />

        <div className="bg-white border border-ink-line rounded-lg p-6">
          <div className="text-center mb-4">
            {accountingClientName && <div className="text-lg font-medium text-ink mb-1">{accountingClientName}</div>}
            <div className="text-xl font-medium text-ink">AR Aging Summary — {shipmentTypeLabel(shipmentType)}</div>
            <div className="text-sm text-ink-body mt-1">For invoices {rangeLabel}</div>
            <div className="text-xs text-ink-body mt-1">All amounts in Philippine peso (₱)</div>
          </div>

          <div className="overflow-hidden border border-ink-line rounded-lg">
            <table className="min-w-full divide-y divide-ink-line text-sm">
              <thead className="bg-ink-canvas">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-body uppercase tracking-wider">Customer</th>
                  {BUCKET_COLUMNS.map((b) => (
                    <th key={b.key} className="px-4 py-3 text-right text-xs font-medium text-ink-body uppercase tracking-wider">{b.label}</th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-medium text-ink-body uppercase tracking-wider">TOTAL</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-ink-line">
                {isFetching && !data ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 6 }).map((__, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-ink-line/40 rounded animate-pulse" /></td>
                      ))}
                    </tr>
                  ))
                ) : isError ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-danger-500">Failed to load AR aging summary.</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-body">No outstanding AR for this scope.</td></tr>
                ) : (
                  rows.map((row) => (
                    <React.Fragment key={row.customerId}>
                      <tr className="hover:bg-ink-canvas cursor-pointer" onClick={() => toggleCustomer(row.customerId)}>
                        <td className="px-4 py-3 font-medium text-ink">
                          <span className="inline-flex items-center gap-2">
                            <span className="text-ink-body">{expanded[row.customerId] ? '▼' : '▶'}</span>
                            {row.customerName}
                          </span>
                        </td>
                        {BUCKET_COLUMNS.map((b) => (
                          <td key={b.key} className="px-4 py-3 text-right text-ink">{cell(row[b.key])}</td>
                        ))}
                        <td className="px-4 py-3 text-right font-semibold text-ink">{cell(row.total)}</td>
                      </tr>
                      {expanded[row.customerId] && (
                        <CustomerDetailRows customerId={row.customerId} shipmentType={shipmentType}
                          dateFrom={dateFrom} dateTo={dateTo} />
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
              {!isFetching && !isError && rows.length > 0 && (
                <tfoot>
                  <tr className="bg-primary-50 border-t-2 border-primary-200 font-bold">
                    <td className="px-4 py-3 text-ink">Grand Total</td>
                    {BUCKET_COLUMNS.map((b) => (
                      <td key={b.key} className="px-4 py-3 text-right text-ink">{cell(grandTotal[b.key])}</td>
                    ))}
                    <td className="px-4 py-3 text-right text-ink">{cell(grandTotal.total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
